import { useEffect, useState } from "react";
import { useActivationFlash } from "../../hooks/useActivationFlash";
import { formatSessionLabel } from "../../state/draftSessions";
import type { DraftSession } from "../../state/draftSessions";

export interface StudentToolboxProps {
  professorEmail: string;
  studentEmail: string;
  saveStatus: string;
  trackingActive: boolean;
  sessions: DraftSession[];
  /** The saved session currently loaded into the editor, if any. */
  selectedSessionId: string | null;
  professorEmailError?: string | null;
  studentEmailError?: string | null;
  onProfessorEmailChange: (value: string) => void;
  onStudentEmailChange: (value: string) => void;
  onProfessorEmailBlur: (value: string) => void;
  onStudentEmailBlur: (value: string) => void;
  onStartAssignment: () => void;
  onSubmit: () => void;
  submitDisabled: boolean;
  submitDisabledReason: string | null;
  onSaveDraft: () => void;
  onLoadSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  professorEmailRef?: React.RefObject<HTMLInputElement>;
}

export function StudentToolbox(props: StudentToolboxProps) {
  const [expanded, setExpanded] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const flash = useActivationFlash();

  const sessionIds = new Set(props.sessions.map((session) => session.id));
  useEffect(() => {
    if (selectedSessionId && !sessionIds.has(selectedSessionId)) {
      setSelectedSessionId(null);
    }
  }, [props.sessions]); // eslint-disable-line react-hooks/exhaustive-deps

  // The provider owns the loaded session (deleting the current one auto-loads
  // the newest remaining, or clears to a fresh draft). Mirror that selection so
  // the dropdown always names the session the editor is actually showing.
  useEffect(() => {
    setSelectedSessionId(props.selectedSessionId);
  }, [props.selectedSessionId]);

  const handleSessionSelect = (id: string) => {
    if (!id) {
      setSelectedSessionId(null);
      return;
    }
    setSelectedSessionId(id);
    props.onLoadSession(id);
  };

  const handleDeleteSelected = () => {
    if (!selectedSessionId) return;
    props.onDeleteSession(selectedSessionId);
  };

  return (
    <div className={`assignment-header ${expanded ? "" : "is-collapsed"}`.trim()}>
      <div className="assignment-header-bar">
        <h2>Student Toolbox</h2>
        <button
          className="ghost-button"
          type="button"
          aria-expanded={expanded}
          aria-controls="student-toolbox-content"
          onClick={(event) => {
            flash(event.currentTarget);
            setExpanded((value) => !value);
          }}
        >
          {expanded ? "Hide Toolbox" : "Show Toolbox"}
        </button>
      </div>

      <div
        className={`assignment-header-body ${expanded ? "" : "hidden"}`.trim()}
        id="student-toolbox-content"
      >
        <div className="assignment-header-top">
          <div className="assignment-header-copy">
            <p className="assignment-copy">
              Everything for this assignment — your prompt, draft, writing session, and saved work —
              stays together in one place.
            </p>
          </div>
          <div className="toolbox-row toolbox-meta-row">
            <div className="code-field">
              <label htmlFor="professor-email-input">
                <span className="micro-label">Professor's Sinclair email</span>
                <input
                  id="professor-email-input"
                  ref={props.professorEmailRef}
                  type="text"
                  placeholder="name@sinclair.edu"
                  autoComplete="off"
                  className={props.professorEmailError ? "has-error" : undefined}
                  aria-invalid={props.professorEmailError ? true : undefined}
                  aria-describedby={
                    props.professorEmailError ? "professor-email-error" : undefined
                  }
                  value={props.professorEmail}
                  onChange={(event) => props.onProfessorEmailChange(event.target.value)}
                  onBlur={(event) => props.onProfessorEmailBlur(event.target.value)}
                />
              </label>
              <span
                id="professor-email-error"
                className="field-error"
                role={props.professorEmailError ? "alert" : undefined}
              >
                {props.professorEmailError}
              </span>
            </div>
            <div className="code-field">
              <label htmlFor="student-email-input">
                <span className="micro-label">Your Sinclair Email</span>
                <input
                  id="student-email-input"
                  type="text"
                  placeholder="you@sinclair.edu"
                  autoComplete="off"
                  className={props.studentEmailError ? "has-error" : undefined}
                  aria-invalid={props.studentEmailError ? true : undefined}
                  aria-describedby={
                    props.studentEmailError ? "student-email-error" : undefined
                  }
                  value={props.studentEmail}
                  onChange={(event) => props.onStudentEmailChange(event.target.value)}
                  onBlur={(event) => props.onStudentEmailBlur(event.target.value)}
                />
              </label>
              <span
                id="student-email-error"
                className="field-error"
                role={props.studentEmailError ? "alert" : undefined}
              >
                {props.studentEmailError}
              </span>
            </div>
            <div className="code-field sessions-field">
              <label htmlFor="saved-sessions-select">
                <span className="micro-label">Saved sessions</span>
              </label>
              <div className="sessions-control">
                <select
                  id="saved-sessions-select"
                  className="sessions-select"
                  disabled={props.sessions.length === 0}
                  aria-label="Saved sessions"
                  value={selectedSessionId ?? ""}
                  onChange={(event) => handleSessionSelect(event.target.value)}
                >
                  <option value="" disabled>
                    {props.sessions.length === 0 ? "No saved sessions" : "Select a session…"}
                  </option>
                  {props.sessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {formatSessionLabel(session)}
                    </option>
                  ))}
                </select>
                <button
                  className="icon-button sessions-delete-button"
                  type="button"
                  aria-label="Delete saved session"
                  title="Delete the selected saved session"
                  disabled={!selectedSessionId}
                  onClick={(event) => {
                    flash(event.currentTarget);
                    handleDeleteSelected();
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                </button>
              </div>
              <span className="field-error" aria-hidden="true" />
            </div>
          </div>
        </div>

        <div className="assignment-actions toolbox-button-row">
          {props.trackingActive ? (
            <span className="session-status-pill" role="status">
              <span className="session-status-dot" aria-hidden="true" />
              Writing Session On
            </span>
          ) : (
            <button
              className="primary-button"
              type="button"
              onClick={(event) => {
                flash(event.currentTarget);
                props.onStartAssignment();
              }}
            >
              Start This Assignment
            </button>
          )}
          <button
            className="primary-button submit-button"
            type="button"
            aria-label="Submit assignment"
            disabled={props.submitDisabled}
            title={props.submitDisabledReason ?? undefined}
            // Keep focus in the editor, as the formatting toolbar does:
            // submitting must not look like the student leaving the writing
            // area, and the blur's stale sync must not race the submission.
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              if (props.submitDisabled) return;
              flash(event.currentTarget);
              props.onSubmit();
            }}
          >
            Submit Assignment
          </button>
          <div className="toolbox-save-group">
            <button
              className="ghost-button save-draft-button"
              type="button"
              aria-label="Save draft now"
              onClick={(event) => {
                flash(event.currentTarget);
                props.onSaveDraft();
              }}
            >
              Save Draft
            </button>
            <span className="save-status-text" role="status" aria-live="polite">
              Save status: {props.saveStatus}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
